import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Package, Clock, CheckCircle, AlertCircle, RefreshCw, 
  Truck, ChevronDown, ChevronUp, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import * as categoryApi from '@/services/categoryApi';

const SectionShipmentRequests = ({ sectionName, sectionIcon: SectionIcon, backRoute }) => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('approved');

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const data = await categoryApi.getSectionIncomingRequests(sectionName);
      setRequests(data.requests || []);
    } catch (error) {
      console.error('Failed to fetch shipment requests:', error);
      toast.error('Failed to load shipment requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [sectionName]);

  const handleAcknowledge = async (requestId) => {
    try {
      await categoryApi.acknowledgeShipmentRequest(requestId);
      toast.success('Shipment request acknowledged');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to acknowledge request');
    }
  };

  const handleComplete = async (requestId) => {
    try {
      await categoryApi.completeShipmentRequest(requestId);
      toast.success('Shipment request marked as completed');
      fetchRequests();
      setShowDetailDialog(false);
    } catch (error) {
      toast.error('Failed to complete request');
    }
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-700 border-red-200',
      normal: 'bg-blue-100 text-blue-700 border-blue-200',
      low: 'bg-gray-100 text-gray-700 border-gray-200'
    };
    return colors[priority] || colors.normal;
  };

  const getStatusBadge = (status) => {
    const colors = {
      approved: 'bg-yellow-100 text-yellow-700',
      acknowledged: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const filteredRequests = requests.filter(r => {
    if (activeTab === 'approved') return r.status === 'approved';
    if (activeTab === 'acknowledged') return r.status === 'acknowledged';
    if (activeTab === 'completed') return r.status === 'completed';
    return true;
  });

  const stats = {
    approved: requests.filter(r => r.status === 'approved').length,
    acknowledged: requests.filter(r => r.status === 'acknowledged').length,
    completed: requests.filter(r => r.status === 'completed').length,
    total: requests.length
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate(backRoute)} 
              className="text-[#F9FAFB] hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Truck className="w-6 h-6 text-[#E8DCC8]" />
              <div>
                <h1 className="text-xl font-bold font-display">Shipment Requests</h1>
                <p className="text-sm text-[#E8DCC8]">{sectionName} Section</p>
              </div>
            </div>
          </div>
          <Button 
            variant="ghost" 
            onClick={fetchRequests}
            className="text-[#F9FAFB] hover:bg-white/10"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600">Pending</p>
                  <p className="text-2xl font-bold text-yellow-700">{stats.approved}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Acknowledged</p>
                  <p className="text-2xl font-bold text-blue-700">{stats.acknowledged}</p>
                </div>
                <Eye className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Completed</p>
                  <p className="text-2xl font-bold text-green-700">{stats.completed}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total</p>
                  <p className="text-2xl font-bold text-gray-700">{stats.total}</p>
                </div>
                <Package className="w-8 h-8 text-gray-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="approved" className="gap-2">
              Pending <Badge variant="secondary">{stats.approved}</Badge>
            </TabsTrigger>
            <TabsTrigger value="acknowledged" className="gap-2">
              Acknowledged <Badge variant="secondary">{stats.acknowledged}</Badge>
            </TabsTrigger>
            <TabsTrigger value="completed" className="gap-2">
              Completed <Badge variant="secondary">{stats.completed}</Badge>
            </TabsTrigger>
            <TabsTrigger value="all">All</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Requests List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : filteredRequests.length === 0 ? (
          <Card className="py-12">
            <CardContent className="flex flex-col items-center justify-center text-center">
              <Package className="w-12 h-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-1">No Requests Found</h3>
              <p className="text-sm text-gray-500">
                {activeTab === 'all' 
                  ? 'No shipment requests for this section yet.' 
                  : `No ${activeTab} requests.`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredRequests.map(request => (
              <Card key={request.request_id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-mono text-sm font-medium">{request.request_id}</span>
                        <Badge className={getPriorityBadge(request.priority)}>
                          {request.priority?.toUpperCase() || 'NORMAL'}
                        </Badge>
                        <Badge className={getStatusBadge(request.status)}>
                          {request.status?.toUpperCase()}
                        </Badge>
                      </div>
                      
                      <div className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">From:</span> {request.created_by_name || 'Category Incharge'} | 
                        <span className="font-medium ml-2">Module:</span> {request.module} | 
                        <span className="font-medium ml-2">Items:</span> {request.items?.length || 0} | 
                        <span className="font-medium ml-2">Total Qty:</span> {request.total_shipment_qty}
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        {request.items?.slice(0, 3).map((item, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {item.design_name} ({item.barcode}) x{item.shipment_qty}
                          </Badge>
                        ))}
                        {request.items?.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{request.items.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-2 ml-4">
                      {request.status === 'approved' && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleAcknowledge(request.request_id)}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Acknowledge
                        </Button>
                      )}
                      <Button 
                        size="sm"
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowDetailDialog(true);
                        }}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Shipment Request: {selectedRequest?.request_id}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <Badge className={getStatusBadge(selectedRequest.status)}>
                    {selectedRequest.status?.toUpperCase()}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Priority</p>
                  <Badge className={getPriorityBadge(selectedRequest.priority)}>
                    {selectedRequest.priority?.toUpperCase() || 'NORMAL'}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Created By</p>
                  <p className="font-medium">{selectedRequest.created_by_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Approved By</p>
                  <p className="font-medium">{selectedRequest.approved_by_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Module</p>
                  <p className="font-medium">{selectedRequest.module}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Quantity</p>
                  <p className="font-medium">{selectedRequest.total_shipment_qty}</p>
                </div>
                {selectedRequest.remarks && (
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500">Remarks</p>
                    <p className="font-medium">{selectedRequest.remarks}</p>
                  </div>
                )}
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Items ({selectedRequest.items?.length})</h4>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Barcode</TableHead>
                        <TableHead>Design Name</TableHead>
                        <TableHead>Color/Shade</TableHead>
                        <TableHead className="text-right">Closing Qty</TableHead>
                        <TableHead className="text-right">Ship Qty</TableHead>
                        <TableHead>Target FG</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedRequest.items?.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono text-sm">{item.barcode}</TableCell>
                          <TableCell>{item.design_name}</TableCell>
                          <TableCell>{item.color}</TableCell>
                          <TableCell className="text-right">{item.closing_qty}</TableCell>
                          <TableCell className="text-right font-medium">{item.shipment_qty}</TableCell>
                          <TableCell>{item.target_fg_name || item.design_name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Close
            </Button>
            {selectedRequest?.status === 'approved' && (
              <Button 
                variant="outline"
                onClick={() => handleAcknowledge(selectedRequest.request_id)}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Acknowledge
              </Button>
            )}
            {(selectedRequest?.status === 'approved' || selectedRequest?.status === 'acknowledged') && (
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={() => handleComplete(selectedRequest.request_id)}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Mark as Completed
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SectionShipmentRequests;
